import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { data, portal, type ClientOrg } from "@aawaz/api-client";
import { Icon, type IconName } from "@aawaz/ui";

export interface PaletteAction { label: string; to: string; icon: IconName; hint?: string }

/** Ctrl/⌘ K: jump to any page or client by typing. */
export function CommandPalette({ open, onClose, pages }: { open: boolean; onClose: () => void; pages: PaletteAction[] }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [i, setI] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const clients = useQuery({ enabled: open, queryKey: ["clients"], queryFn: () => data<{ data: ClientOrg[] }>(portal.GET("/portal/v1/clients")) });
  const items = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const match = (s: string) => !needle || s.toLowerCase().includes(needle);
    const p = pages.filter((a) => match(a.label)).map((a) => ({ ...a, group: "Go to" }));
    const c = (clients.data?.data ?? []).filter((x) => match(x.display_name) || match(x.external_ref) || (x.gstins ?? []).some((g) => match(g)))
      .slice(0, 8).map((x) => ({ label: x.display_name, to: `/clients/${x.id}`, icon: "building" as IconName, hint: x.gstins?.[0] ?? x.external_ref, group: "Clients" }));
    return [...p, ...c];
  }, [q, pages, clients.data]);
  useEffect(() => { if (open) { setQ(""); setI(0); setTimeout(() => input.current?.focus(), 0); } }, [open]);
  useEffect(() => { setI(0); }, [q]);
  if (!open) return null;
  const go = (to: string) => { onClose(); nav(to); };
  let group = "";
  return (
    <div className="ui-palette" role="dialog" aria-modal="true" aria-label="Search">
      <div className="ui-drawer-scrim" onClick={onClose} />
      <div className="ui-palette-box">
        <div className="ui-input-icon">
          <Icon name="search" size={18} />
          <input ref={input} className="ui-input" placeholder="Search pages and clients…" value={q} aria-label="Search"
                 onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => {
                   if (e.key === "Escape") onClose();
                   else if (e.key === "ArrowDown") { e.preventDefault(); setI((n) => Math.min(n + 1, items.length - 1)); }
                   else if (e.key === "ArrowUp") { e.preventDefault(); setI((n) => Math.max(n - 1, 0)); }
                   else if (e.key === "Enter" && items[i]) go(items[i]!.to);
                 }} />
        </div>
        <div className="ui-palette-list" role="listbox">
          {items.length === 0 ? <p className="ui-empty">Nothing matches “{q}”.</p> : items.map((a, n) => {
            const head = a.group !== group ? a.group : null;
            group = a.group;
            return (
              <div key={`${a.group}-${a.to}`}>
                {head ? <div className="ui-palette-group">{head}</div> : null}
                <button className="ui-palette-item" role="option" aria-selected={n === i} onMouseEnter={() => setI(n)} onClick={() => go(a.to)}>
                  <Icon name={a.icon} size={16} />{a.label}{a.hint ? <small>{a.hint}</small> : null}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
